import { useState } from "react";
import type { FormEvent } from "react";

import { isAPIError } from "@/core/services";
import { REQUIRED_MESSAGE, useDialogForm, useToast } from "@/ds";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { criarTipoObra } from "@/modules/financeiro-obras";
import type {
  CentroCustoOpcao,
  DominanciaTipoObra,
  NovoTipoObraRequest,
} from "@/modules/financeiro-obras";
import { JUSTIFICATIVA_OBRIGATORIA } from "@/shared/components";
import { FEL_ZERO } from "../planejamento-controle.cancelamento";
import type { DominanciaBlocoProps } from "../dominancia-bloco";
import {
  ADICIONADO_MESSAGE,
  ADICIONAR_LABEL,
  AVISO_GMUD,
  CENTRO_CUSTO_INDISPONIVEL_PLACEHOLDER,
  CENTRO_CUSTO_LABEL,
  CENTRO_CUSTO_PLACEHOLDER,
  CENTRO_CUSTO_UNICO_AJUDA,
  CONFIRMAR_SUGERIDO_DESCRICAO,
  CONFIRMAR_SUGERIDO_LABEL,
  DOMINANTE_ESCOLHA_LABEL,
  DOMINANTE_ESCOLHA_OBRIGATORIA,
  DOMINANTE_ESCOLHA_PLACEHOLDER,
  DOMINANTE_LABEL,
  DOMINANTE_TOOLTIP,
  EM_APROVACAO_TAG,
  ENVIADO_PARA_APROVACAO,
  ESCOLHER_OUTRO_DESCRICAO,
  ESCOLHER_OUTRO_LABEL,
  GRUPOS_NATUREZA,
  MANTER_DESCRICAO,
  MANTER_LABEL,
  NOVO_TIPO_FORM_ID,
  NOVO_TIPO_VALOR,
  SEM_TIPO_ELEGIVEL,
  SOLICITAR_LABEL,
  TIPO_OBRA_ESCOLHA_CENTRO_PLACEHOLDER,
  TIPO_OBRA_PLACEHOLDER,
  TROCAR_DESCRICAO,
  TROCAR_PARA_LABEL,
} from "./novo-tipo-obra-dialog.constants";
import type {
  EscolhaNovoTipo,
  GrupoSelecao,
  NovoTipoObraDialogProps,
  NovoTipoObraEstado,
  NovoTipoObraForm,
  OpcaoSelecao,
} from "./types-novo-tipo-obra-dialog";

const INICIAL: NovoTipoObraForm = {
  centroCustoId: "",
  tipoObraId: "",
  dominancia: "MANTER",
  dominanteEscolhido: "",
  justificativa: "",
};

const rotulo = (item: { codigo: string; nome: string }) => `${item.codigo} · ${item.nome}`;
const rotuloCentro = (item: CentroCustoOpcao) => `${item.nome} · ${item.codigo}`;

const ORDEM_NATUREZA = Object.keys(GRUPOS_NATUREZA) as (keyof typeof GRUPOS_NATUREZA)[];

function agruparCentros(centros: CentroCustoOpcao[]): GrupoSelecao[] {
  return ORDEM_NATUREZA.map((natureza) => ({
    label: GRUPOS_NATUREZA[natureza],
    items: centros
      .filter((centro) => centro.natureza === natureza)
      .map((centro): OpcaoSelecao => ({ label: rotuloCentro(centro), value: centro.id })),
  })).filter((grupo) => grupo.items.length > 0);
}

export function useNovoTipoObraDialog(props: NovoTipoObraDialogProps) {
  const { fel, opcoes, onClose } = props;
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const descarte = useConfirmarDescarte({ onDescartar: onClose, testId: "novo-tipo-descarte" });
  const [erroTipoServidor, setErroTipoServidor] = useState<string | null>(null);

  const escolhendo = props.onEscolher !== undefined;
  const efetivacaoImediata = escolhendo || fel === FEL_ZERO;
  const dados = opcoes.data;
  const centros: CentroCustoOpcao[] = (dados?.centrosCusto ?? []).filter(
    (centro) => centro.elegivel,
  );
  const centroUnico = centros.length === 1 ? centros[0] : undefined;

  let estado: NovoTipoObraEstado = "pronto";
  if (opcoes.status === "forbidden") estado = "proibido";
  else if (opcoes.status === "error") estado = "erro";
  else if (opcoes.status === "loading" || !dados) estado = "carregando";
  else if (centros.length === 0) estado = "sem-centro-custo";
  const semCentro = estado === "sem-centro-custo";

  const form = useDialogForm<NovoTipoObraForm>({
    initial: INICIAL,
    validate: (valores) => {
      const erros: Partial<Record<keyof NovoTipoObraForm, string>> = {};
      if (!(centroUnico?.id ?? valores.centroCustoId)) erros.centroCustoId = REQUIRED_MESSAGE;
      if (!valores.tipoObraId) erros.tipoObraId = REQUIRED_MESSAGE;
      if (trocando && semDominante && valores.dominanteEscolhido === "")
        erros.dominanteEscolhido = DOMINANTE_ESCOLHA_OBRIGATORIA;
      return erros;
    },
    onSubmit: async (valores) => {
      try {
        const body = {
          centroCustoId: centroUnico?.id ?? valores.centroCustoId,
          tipoObraId: valores.tipoObraId,
          dominancia: trocando ? ("TROCAR" as const) : ("MANTER" as const),
          ...(trocando ? { justificativa: valores.justificativa.trim() } : {}),
          ...(trocando && semDominante ? escolhaDoDominante(valores.dominanteEscolhido) : {}),
        };
        if (props.onEscolher) {
          props.onEscolher(escolhaDoSubfluxo(body));
          return;
        }
        const { obraId, onConcluido } = props;
        const resposta = await comChave(body, (idempotencyKey) =>
          criarTipoObra(obraId, body, { idempotencyKey }),
        );
        const nome = rotulo(tipoSelecionado ?? { codigo: "", nome: "" });
        if (resposta.situacao === "APROVADO") toast.success(ADICIONADO_MESSAGE(nome));
        else toast.info(ENVIADO_PARA_APROVACAO(nome));
        onConcluido();
      } catch (error) {
        if (isAPIError(error) && error.status === 409) {
          setErroTipoServidor(error.message);
          return;
        }
        throw error;
      }
    },
  });

  function escolhaDoSubfluxo(pacote: NovoTipoObraRequest): EscolhaNovoTipo {
    return {
      pacote,
      centroCusto: {
        id: centro?.id ?? pacote.centroCustoId,
        rotulo: centro ? rotuloCentro(centro) : "",
        principal: centro?.principal === true,
      },
      tipo: {
        id: pacote.tipoObraId,
        codigo: tipoSelecionado?.codigo ?? "",
        rotulo: tipoSelecionado ? rotulo(tipoSelecionado) : "",
        fonteEscopo: tipoSelecionado?.fonteEscopo ?? "LIVRE",
      },
    };
  }

  const { values } = form;
  const centroCustoId = centroUnico?.id ?? values.centroCustoId;
  const centro = centros.find((item) => item.id === centroCustoId) ?? null;
  const tiposDoCentro = (centro?.tiposObraElegiveis ?? []).filter(
    (tipo) => tipo.bloqueio === null || tipo.bloqueio === "EM_APROVACAO",
  );
  const tipos = tiposDoCentro.filter((tipo) => tipo.bloqueio === null);
  const tipoSelecionado = tipos.find((tipo) => tipo.id === values.tipoObraId) ?? null;

  const semDominante = dados?.semDominante === true;
  const atual = dados?.dominanteAtual?.tipoObra ?? null;
  const sugerido = tipoSelecionado?.dominanteSugeridoSeIncluido ?? null;
  const noPrincipal = centro?.principal === true && tipoSelecionado !== null;

  const passaNaFrente =
    noPrincipal && !semDominante && atual !== null && sugerido?.id === tipoSelecionado?.id;
  const pedeConfirmacao = noPrincipal && semDominante && sugerido !== null;

  const candidatosEscolha: OpcaoSelecao[] = pedeConfirmacao
    ? [
        ...(centro?.dominantesCandidatos ?? []).map((item) => ({
          label: rotulo(item.tipoObra),
          value: item.obraTipoObraId,
          tipoObraId: item.tipoObra.id,
        })),
        ...(tipoSelecionado
          ? [
              {
                label: rotulo(tipoSelecionado),
                value: NOVO_TIPO_VALOR,
                tipoObraId: tipoSelecionado.id,
              },
            ]
          : []),
      ]
        .filter((item) => item.tipoObraId !== sugerido?.id)
        .map(({ label, value }) => ({ label, value }))
    : [];

  const trocando = (passaNaFrente || pedeConfirmacao) && values.dominancia === "TROCAR";
  const justificativaVazia = values.justificativa.trim() === "";

  function limparDecisao() {
    form.setValue("dominancia", "MANTER");
    form.setValue("dominanteEscolhido", "");
    form.setValue("justificativa", "");
  }

  function limparDependentes() {
    form.setValue("tipoObraId", "");
    limparDecisao();
  }

  function cancelar() {
    descarte.pedir(form.isDirty);
  }

  let decisao: DominanciaBlocoProps | null = null;
  if (passaNaFrente && atual && tipoSelecionado) {
    decisao = {
      ...blocoBase(),
      cartoes: {
        ...cartoesBase(),
        itens: [
          {
            value: "MANTER",
            titulo: MANTER_LABEL,
            destaque: rotulo(atual),
            descricao: MANTER_DESCRICAO,
            testId: "novo-tipo-manter",
            destaqueTestId: "novo-tipo-dominante-atual",
          },
          {
            value: "TROCAR",
            titulo: TROCAR_PARA_LABEL,
            destaque: rotulo(tipoSelecionado),
            descricao: TROCAR_DESCRICAO(`${tipoSelecionado.codigo} vem antes de ${atual.codigo}`),
            testId: "novo-tipo-trocar",
            destaqueTestId: "novo-tipo-dominante-novo",
          },
        ],
      },
      seletor: null,
    };
  } else if (pedeConfirmacao && sugerido) {
    decisao = {
      ...blocoBase(),
      cartoes: {
        ...cartoesBase(),
        itens: [
          {
            value: "MANTER",
            titulo: CONFIRMAR_SUGERIDO_LABEL,
            destaque: rotulo(sugerido),
            descricao: CONFIRMAR_SUGERIDO_DESCRICAO,
            testId: "novo-tipo-manter",
            destaqueTestId: "novo-tipo-dominante-sugerido",
          },
          ...(candidatosEscolha.length > 0
            ? [
                {
                  value: "TROCAR",
                  titulo: ESCOLHER_OUTRO_LABEL,
                  destaque: null,
                  descricao: ESCOLHER_OUTRO_DESCRICAO,
                  testId: "novo-tipo-trocar",
                  destaqueTestId: "novo-tipo-dominante-outro",
                },
              ]
            : []),
        ],
      },
      seletor: trocando
        ? {
            label: DOMINANTE_ESCOLHA_LABEL,
            placeholder: DOMINANTE_ESCOLHA_PLACEHOLDER,
            value: values.dominanteEscolhido || null,
            opcoes: candidatosEscolha,
            ajuda: null,
            error: form.errors.dominanteEscolhido ?? null,
            travado: false,
            onChange: (valor: string) => form.setValue("dominanteEscolhido", valor),
            testId: "novo-tipo-dominante-escolha",
            errorTestId: "novo-tipo-dominante-escolha-error",
          }
        : null,
    };
  }

  function blocoBase() {
    return {
      titulo: { texto: DOMINANTE_LABEL, ajuda: DOMINANTE_TOOLTIP },
      aviso: null,
      disabled: form.isSubmitting,
      testId: "novo-tipo-decisao",
    };
  }

  function cartoesBase() {
    return {
      value: values.dominancia,
      onChange: (valor: string) => form.setValue("dominancia", valor as DominanciaTipoObra),
      testId: "novo-tipo-dominancia",
      label: DOMINANTE_LABEL,
    };
  }

  const tiposParaSelecao: OpcaoSelecao[] = tiposDoCentro.map((item) => ({
    label: rotulo(item),
    value: item.id,
    disabled: item.bloqueio === "EM_APROVACAO",
    tag: item.bloqueio === "EM_APROVACAO" ? EM_APROVACAO_TAG : null,
  }));

  const escolhaPendente =
    trocando && semDominante && (values.dominanteEscolhido === "" || justificativaVazia);

  return {
    formId: NOVO_TIPO_FORM_ID,
    estado,
    errorMessage: opcoes.errorMessage,
    recarregar: opcoes.reload,
    aviso: efetivacaoImediata || semCentro ? null : AVISO_GMUD(fel),
    confirmarLabel: efetivacaoImediata ? ADICIONAR_LABEL : SOLICITAR_LABEL,
    centroCusto: {
      label: CENTRO_CUSTO_LABEL,
      value: centroCustoId || null,
      grupos: centros.length > 1 ? agruparCentros(centros) : [],
      opcoes: centros.map((item): OpcaoSelecao => ({ label: rotuloCentro(item), value: item.id })),
      placeholder: semCentro ? CENTRO_CUSTO_INDISPONIVEL_PLACEHOLDER : CENTRO_CUSTO_PLACEHOLDER,
      // Com um só Centro de Custo elegível, a identificação fica preenchida e sem escolha.
      travado: centroUnico !== undefined || semCentro,
      ajuda: centroUnico !== undefined ? CENTRO_CUSTO_UNICO_AJUDA : null,
      error: form.errors.centroCustoId ?? null,
    },
    tipoObra: {
      value: values.tipoObraId || null,
      opcoes: tiposParaSelecao,
      semOpcao: centro !== null && tipos.length === 0 ? SEM_TIPO_ELEGIVEL : null,
      error: form.errors.tipoObraId ?? erroTipoServidor,
      placeholder: centro === null ? TIPO_OBRA_ESCOLHA_CENTRO_PLACEHOLDER : TIPO_OBRA_PLACEHOLDER,
      desabilitado: centro === null,
    },
    decisao,
    justificativa:
      decisao && trocando
        ? {
            value: values.justificativa,
            error: justificativaVazia ? JUSTIFICATIVA_OBRIGATORIA : null,
          }
        : null,
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    adicionarDesabilitado:
      estado !== "pronto" || (trocando && justificativaVazia) || escolhaPendente,
    adicionarMotivo:
      estado === "pronto" && trocando && justificativaVazia ? JUSTIFICATIVA_OBRIGATORIA : null,
    escolherCentroCusto: (id: string) => {
      setErroTipoServidor(null);
      form.setValue("centroCustoId", id);
      limparDependentes();
    },
    escolherTipoObra: (id: string) => {
      setErroTipoServidor(null);
      form.setValue("tipoObraId", id);
      limparDecisao();
    },
    digitarJustificativa: (texto: string) => form.setValue("justificativa", texto),
    enviar: (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErroTipoServidor(null);
      void form.submit();
    },
    cancelar,
    descarte: descarte.confirmacao,
  };
}

function escolhaDoDominante(escolhido: string) {
  return escolhido === NOVO_TIPO_VALOR
    ? { novoTipoComoDominante: true }
    : { dominanteEscolhidoObraTipoObraId: escolhido };
}
